"use client"

import { graphql, useClient, useMutation } from "@opensea/graphql"
import { useRouter } from "@opensea/navigation"
import {
  Avatar,
  Dropdown,
  DropdownItem,
  DropdownItemContent,
  DropdownItemTitle,
} from "@opensea/next-ui-kit"
import {
  Chip,
  classNames,
  FlexCenter,
  FlexColumn,
  formatAddress,
  TextBody,
  TextOverflowTooltip,
  Tooltip,
  UnstyledButton,
  useToasts,
} from "@opensea/ui-kit"
import { Check, LinkOff, MoreHoriz, Star } from "@opensea/ui-kit/icons"
import type { Address } from "@opensea/utils"
import { isAddressEqual, normalizeAddress } from "@opensea/utils"
import {
  getChainArchFromAddress,
  useConnect,
  useConnections,
  usePreviouslyConnectedAccounts,
} from "@opensea/wallet"
import { keyBy } from "es-toolkit"
import { useTranslations } from "next-intl"
import type { ReactNode } from "react"
import { useCallback, useMemo } from "react"
import { prefetchProfileItemsPage } from "@/app/[locale]/[currency]/[persona]/(full-immersive)/profile/[heroHeaderView]/[identifier]/items/[userPreferences]/prefetch"
import { prefetchProfileTokensPage } from "@/app/[locale]/[currency]/[persona]/(full-immersive)/profile/[heroHeaderView]/[identifier]/tokens/[userPreferences]/prefetch"
import { getProfileUrlByIdentifier } from "@/components/profile/utils"
import { useAddress } from "@/hooks/wallet"
import { extractGraphQlErrorMessage } from "@/lib/graphql"
import { accountsAdapter } from "@/providers/WalletProvider/accounts-adapter"
import {
  AccountSwitcherItem,
  AccountSwitcherItemAvatar,
  AccountSwitcherItemAvatarBadge,
  AccountSwitcherItemContent,
  AccountSwitcherItemDescription,
  AccountSwitcherItemSide,
  AccountSwitcherItemTitle,
} from "./AccountSwitcherItem"

const MakePrimaryWalletMutation = graphql(`
  mutation MakePrimaryWallet($address: Address!) {
    replaceAccountPrimaryWallet(address: $address)
  }
`)

type Props = {
  address: Address
  description?: ReactNode
  onClick?: () => void
  className?: string
  avatarSize?: number
  showCheck?: boolean
  isLink?: boolean
  showActions?: boolean
  onUnlink?: () => unknown
  onMakePrimary?: () => unknown
  imageUrl?: string | undefined | null
  isPrimary?: boolean
  showNotLinked?: boolean
  alreadyLinked?: boolean
  connected?: boolean
  connectorId?: string | null
  variant: "full" | "compact"
  displayName?: string | null
}

export function LinkedAccountSwitcherItem({
  address,
  displayName,
  description,
  onClick,
  className,
  avatarSize,
  showCheck,
  isLink = true,
  showActions = false,
  onUnlink,
  onMakePrimary,
  imageUrl,
  isPrimary,
  showNotLinked = false,
  alreadyLinked = false,
  connected = false,
  connectorId,
  variant,
}: Props) {
  const client = useClient()
  const router = useRouter()
  const connections = useConnections()
  const { activeConnector, getConnector } = useConnect()
  const activeAddress = useAddress()
  const { showErrorMessage, showSuccessMessage } = useToasts()
  const t = useTranslations("LinkedAccountSwitcherItem")

  const [, makePrimaryWallet] = useMutation(MakePrimaryWalletMutation)

  const normalized = normalizeAddress(address)
  const connectedWalletsByAddress = new Map(
    connections.flatMap(connection =>
      connection.addresses.map(address => [
        normalizeAddress(address),
        connection,
      ]),
    ),
  )
  const previouslyConnectedAccounts = usePreviouslyConnectedAccounts()
  const previouslyConnectedAccountsByAddress = useMemo(
    () =>
      keyBy(previouslyConnectedAccounts, account =>
        normalizeAddress(account.address),
      ),
    [previouslyConnectedAccounts],
  )
  const previouslyConnectedAccount =
    previouslyConnectedAccountsByAddress[normalized]
  const previousConnector = previouslyConnectedAccount
    ? getConnector(previouslyConnectedAccount.connectorId)
    : undefined

  const connection = connectedWalletsByAddress.get(normalized)

  // Determine the connector to use for badge icon, prioritizing connectorId prop
  const defaultConnector = connectorId ? getConnector(connectorId) : undefined
  const badgeIcon =
    connection?.connector.icon ||
    defaultConnector?.icon ||
    previousConnector?.icon
  const profileHref = getProfileUrlByIdentifier(normalized)
  const isActive =
    connection?.connector.id === activeConnector?.id &&
    isAddressEqual(address, activeAddress || "")

  const handleUnlinkWallet = useCallback(
    async (walletAddress: Address) => {
      // Check if trying to unlink the currently connected wallet
      if (isActive) {
        showErrorMessage(t("cannotUnlinkConnectedWallet"))
        return
      }

      try {
        const result = await accountsAdapter.unlinkWallet({ walletAddress })

        if (!result.success) {
          throw new Error(result.message || "Failed to unlink wallet")
        }

        showSuccessMessage(t("walletUnlinkedSuccessfully"))
        await onUnlink?.()
      } catch (error) {
        showErrorMessage(
          t("failedToUnlinkWallet", {
            error: error instanceof Error ? error.message : "Unknown error",
          }),
        )
      }
    },
    [showSuccessMessage, showErrorMessage, t, onUnlink, isActive],
  )

  const handleMakePrimary = useCallback(
    async (walletAddress: Address) => {
      try {
        const result = await makePrimaryWallet({
          address: walletAddress,
        })

        if (result.error) {
          showErrorMessage(extractGraphQlErrorMessage(result.error))
          return
        }

        if (result.data?.replaceAccountPrimaryWallet !== false) {
          showSuccessMessage(t("walletMadePrimarySuccessfully"))
          await onMakePrimary?.()
        } else {
          showErrorMessage(
            t("failedToMakePrimaryWallet", { error: "Operation failed" }),
          )
        }
      } catch (error) {
        showErrorMessage(
          t("failedToMakePrimaryWallet", {
            error: error instanceof Error ? error.message : "Unknown error",
          }),
        )
      }
    },
    [makePrimaryWallet, showSuccessMessage, showErrorMessage, t, onMakePrimary],
  )

  return (
    <AccountSwitcherItem
      asChild
      className={className}
      href={isLink ? profileHref : undefined}
      onClick={onClick}
      onPointerOver={() => {
        router.prefetch(profileHref)
        if (getChainArchFromAddress(normalized) === "SVM") {
          prefetchProfileTokensPage(normalized, client)
        } else {
          prefetchProfileItemsPage(normalized, client)
        }
      }}
    >
      <li>
        <AccountSwitcherItemAvatar
          badge={
            badgeIcon ? (
              <AccountSwitcherItemAvatarBadge size="xs">
                <Avatar src={badgeIcon} />
              </AccountSwitcherItemAvatarBadge>
            ) : undefined
          }
          seed={normalized}
          size={avatarSize}
          src={imageUrl || undefined}
        />
        <AccountSwitcherItemContent>
          <FlexColumn className="items-start">
            <AccountSwitcherItemTitle>
              <FlexCenter className="gap-2">
                <TextOverflowTooltip
                  className={
                    variant === "full" ? "max-w-[150px]" : "max-w-[100px]"
                  }
                  disabled
                >
                  {displayName || formatAddress(address)}
                </TextOverflowTooltip>
                {variant === "full" && displayName ? (
                  <TextBody color="text-secondary" size="xs">
                    ({formatAddress(address)})
                  </TextBody>
                ) : null}
                {isPrimary ? (
                  <Tooltip
                    className="w-[320px]"
                    content={t("primaryWalletTooltip")}
                  >
                    <Chip
                      className={classNames(
                        "border-none bg-rare-transparent px-1 text-rare",
                        {
                          "gap-1": variant === "full",
                        },
                      )}
                    >
                      <Star size={14} />
                      {variant === "full" ? t("primary") : null}
                    </Chip>
                  </Tooltip>
                ) : null}
                {connection ? null : (
                  <Tooltip
                    className="w-[320px]"
                    content={t("disconnectedWalletTooltip")}
                  >
                    <Chip
                      className="border-none px-1"
                      color="text-secondary"
                      variant="frosted"
                    >
                      <LinkOff size={14} />
                    </Chip>
                  </Tooltip>
                )}

                {showNotLinked && (
                  <Chip color="text-secondary">{t("notLinked")}</Chip>
                )}
                {connected && (
                  <Chip color="text-secondary">{t("connected")}</Chip>
                )}
                {alreadyLinked && (
                  <Chip color="warning">{t("alreadyLinked")}</Chip>
                )}
              </FlexCenter>
            </AccountSwitcherItemTitle>
            {description ? (
              <AccountSwitcherItemDescription className="flex items-center gap-2">
                {description}
              </AccountSwitcherItemDescription>
            ) : null}
          </FlexColumn>
        </AccountSwitcherItemContent>
        {showCheck && isActive ? (
          <AccountSwitcherItemSide>
            <Check size={20} />
          </AccountSwitcherItemSide>
        ) : null}

        {showActions && !isPrimary ? (
          <AccountSwitcherItemSide>
            <Dropdown
              align="end"
              content={
                <>
                  <DropdownItem onClick={() => handleMakePrimary(normalized)}>
                    <DropdownItemContent>
                      <DropdownItemTitle>{t("makePrimary")}</DropdownItemTitle>
                    </DropdownItemContent>
                  </DropdownItem>
                  <DropdownItem onClick={() => handleUnlinkWallet(normalized)}>
                    <DropdownItemContent>
                      <DropdownItemTitle>{t("unlinkWallet")}</DropdownItemTitle>
                    </DropdownItemContent>
                  </DropdownItem>
                </>
              }
              side="bottom"
            >
              <UnstyledButton>
                <MoreHoriz fill="text-secondary" size={20} />
              </UnstyledButton>
            </Dropdown>
          </AccountSwitcherItemSide>
        ) : null}
      </li>
    </AccountSwitcherItem>
  )
}
